/**
 * Closed LOCI category taxonomy (COR-37).
 *
 * Canonical ten top-level slugs, the old→new successor map every migration
 * keys off, cross-cutting tags (not chip-set members), and latent
 * `format:*` / `audience:*` facet vocabularies used by ranking (no UI this
 * project).
 *
 * `PLATFORM_EVENT_CATEGORIES` in `normalized-event.ts` is this same closed
 * set — keep them identical so Zod / ingestors / seeds cannot drift.
 */

/** Display names for the ten platform roots, keyed by slug. */
export const CANONICAL_CATEGORY_LABELS = {
  music: 'Music',
  food: 'Food',
  drinks: 'Drinks',
  sports: 'Sports',
  art: 'Art',
  'business-networking': 'Business & Networking',
  'science-tech': 'Science & Tech',
  'community-culture': 'Community & Culture',
  wellness: 'Wellness',
  'hobbies-interests': 'Hobbies & Interests',
} as const;

/** Closed set of ten top-level category slugs. */
export const CANONICAL_CATEGORY_SLUGS = [
  'music',
  'food',
  'drinks',
  'sports',
  'art',
  'business-networking',
  'science-tech',
  'community-culture',
  'wellness',
  'hobbies-interests',
] as const;

export type CanonicalCategorySlug = (typeof CANONICAL_CATEGORY_SLUGS)[number];

/**
 * `EventCategory.assignmentMethod` marking an operator's own choice (KTD10).
 *
 * Lives here rather than in the data-pipeline engine because two services
 * write it and neither can import the other: backend-api's admin routes stamp
 * it when a human sets a category, and the data-pipeline categorization engine
 * reads it to decide a primary is pinned and must never be re-derived. A
 * literal drifting apart in one of them would silently un-pin every admin
 * override, so the string has exactly one definition.
 */
export const OPERATOR_CONFIRMED_ASSIGNMENT_METHOD = 'operator-confirmed' as const;

/**
 * Confidence written alongside an operator-confirmed assignment. A human
 * decision is certain by construction, and the value keeps such rows above
 * every confidence-threshold read (exclusivity gating, review queues).
 */
export const OPERATOR_CONFIRMED_CONFIDENCE = 1 as const;

/**
 * Auto-live confidence cutoff (KTD3 starting value — tuned later on measured
 * precision, not queue volume). A primary assignment at or above this bar is
 * the auto-live tier; below it the assignment is a low-confidence best-guess
 * that still counts toward the count-of-record but is excluded from
 * exclusivity attribution (KTD7 / AE4).
 *
 * Lives here for the same reason as {@link OPERATOR_CONFIRMED_ASSIGNMENT_METHOD}:
 * two services read it and neither can import the other. The data-pipeline
 * classifier derives `needsReview` from it, and backend-api's count-of-record
 * helper (API-1639) splits the exclusivity-eligible tier on it. Two literals
 * would let the review queue and the number a sponsor buys against drift apart
 * with no failing test.
 */
export const CATEGORY_AUTO_LIVE_CONFIDENCE = 0.7;

/**
 * Single-successor remap for every retired or renamed slug.
 *
 * - Identity rows for the closed set keep migrations idempotent.
 * - `tech` collapses to `science-tech` (the rename half of the tech split;
 *   `business-networking` is net-new and assigned by the engine, not this map).
 * - Orphan roots (`family`, `entertainment`, COR-1 leftovers) each have one
 *   successor in the closed set.
 */
export const OLD_TO_NEW_CATEGORY: Readonly<Record<string, CanonicalCategorySlug>> = {
  music: 'music',
  food: 'food',
  drinks: 'drinks',
  sports: 'sports',
  art: 'art',
  'business-networking': 'business-networking',
  'science-tech': 'science-tech',
  'community-culture': 'community-culture',
  wellness: 'wellness',
  'hobbies-interests': 'hobbies-interests',
  // Retired / renamed
  tech: 'science-tech',
  family: 'community-culture',
  entertainment: 'art',
  // COR-1-era orphans (defensive — may still appear in saved searches / URLs)
  'arts-culture': 'art',
  'food-drink': 'food',
  business: 'business-networking',
  community: 'community-culture',
};

/**
 * Every slug the taxonomy has explicitly SUPERSEDED — a key of
 * {@link OLD_TO_NEW_CATEGORY} that is not itself one of the closed ten.
 * Today: `tech`, `family`, `entertainment`, and the COR-1-era leftovers.
 *
 * Derived rather than listed, so retiring another root extends every consumer
 * at once instead of leaving one silently short.
 *
 * ## Retired ≠ merely non-canonical
 *
 * The distinction matters and is easy to get wrong. `categories` also holds
 * CHILD rows (`parentId` set) that are not top-level slugs, and the admin
 * event routes accept any `categoryId` — so an operator can legitimately pin a
 * primary on a slug outside the closed ten. Treating "not in the closed ten" as
 * "retired" would therefore un-pin every such override on the next ingest pass
 * and re-derive it from the feed's raw label, discarding a human decision.
 *
 * A slug in THIS set is different: the taxonomy has declared it superseded, so
 * a primary parked on it backs no chip and no `/category/<slug>` page. The U8
 * backfill (PIP-188) re-homes those by content — deliberately by content, not
 * via {@link OLD_TO_NEW_CATEGORY}, which the plan reserves for the scalar
 * URL / saved-search / preference-weight migrations.
 */
export const RETIRED_CATEGORY_SLUGS: readonly string[] = Object.freeze(
  Object.keys(OLD_TO_NEW_CATEGORY)
    .filter((slug) => !(CANONICAL_CATEGORY_SLUGS as readonly string[]).includes(slug))
    .sort(),
);

/** Has the taxonomy explicitly superseded this slug? See {@link RETIRED_CATEGORY_SLUGS}. */
export function isRetiredCategorySlug(slug: string): boolean {
  return RETIRED_CATEGORY_SLUGS.includes(slug);
}

/**
 * Cross-cutting tags. These are NOT chip-set / top-level categories — they
 * decorate events that already have a primary in the closed ten.
 */
export const CATEGORY_TAG_VOCABULARY = ['religion', 'charity', 'seasonal', 'fashion'] as const;

export type CategoryTag = (typeof CATEGORY_TAG_VOCABULARY)[number];

/** Latent format facet values (ranking-feeding; no UI this project). */
export const FORMAT_FACET_VALUES = [
  'workshop',
  'meetup',
  'conference',
  'class',
  'networking',
  'performance',
  'screening',
  'festival',
  'party',
  'tournament',
  'exhibition',
  'other',
] as const;

export type FormatFacetValue = (typeof FORMAT_FACET_VALUES)[number];

/** Latent audience facet values (ranking-feeding; no UI this project). */
export const AUDIENCE_FACET_VALUES = [
  'all-ages',
  'family',
  '18-plus',
  '21-plus',
  'professionals',
  'students',
  'women',
  'lgbtq',
  'seniors',
] as const;

export type AudienceFacetValue = (typeof AUDIENCE_FACET_VALUES)[number];

export const FORMAT_FACET_NAMESPACE = 'format' as const;
export const AUDIENCE_FACET_NAMESPACE = 'audience' as const;

export type FormatFacetToken = `${typeof FORMAT_FACET_NAMESPACE}:${FormatFacetValue}`;
export type AudienceFacetToken = `${typeof AUDIENCE_FACET_NAMESPACE}:${AudienceFacetValue}`;

export function formatFacetToken(value: FormatFacetValue): FormatFacetToken {
  return `${FORMAT_FACET_NAMESPACE}:${value}`;
}

export function audienceFacetToken(value: AudienceFacetValue): AudienceFacetToken {
  return `${AUDIENCE_FACET_NAMESPACE}:${value}`;
}

/** Resolve any known slug to its closed-set successor; `undefined` if unknown. */
export function mapToCanonicalCategory(slug: string): CanonicalCategorySlug | undefined {
  return OLD_TO_NEW_CATEGORY[slug];
}

export function isCanonicalCategorySlug(slug: string): slug is CanonicalCategorySlug {
  return (CANONICAL_CATEGORY_SLUGS as readonly string[]).includes(slug);
}

export function isCategoryTag(tag: string): tag is CategoryTag {
  return (CATEGORY_TAG_VOCABULARY as readonly string[]).includes(tag);
}
